import React, { useState, useContext } from "react";
import {
  Box,
  Avatar,
  Typography,
  Stack,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  Collapse,
  IconButton,
  FormControl,
  MenuItem,
  Select,
  Tooltip
} from "@mui/material";
import {
  LogOut,
  Moon,
  Sun,
  User as UserIcon,
  Timer,
  AlertTriangle
} from "lucide-react";
import { useLogout, useGetLocale, useSetLocale, useTranslate } from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import { ColorModeContext } from "../../contexts/color-mode";
import i18n from "../../I18n";
import { useNavigate } from "react-router-dom";
import { getTrialDaysRemaining, isTrialExpired } from "../../utils/trial-permissions";

interface SiderFooterProps {
  collapsed?: boolean;
}

export const SiderFooter: React.FC<SiderFooterProps> = ({ collapsed = false }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mutate: logout } = useLogout();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const translate = useTranslate();

  // Color mode context
  const { mode, setMode } = useContext(ColorModeContext);

  // Language context
  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();

  const handleClick = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'MODERATOR':
        return 'warning';
      case 'SAFEE_MODERATOR':
        return 'warning';
      case 'USER':
      default:
        return 'primary';
    }
  };

  if (!user) return null;

  // Trial info for MODERATOR_FREE users
  const daysRemaining = user.role === 'MODERATOR_FREE' 
    ? getTrialDaysRemaining(user.freeTrialStartedAt || null) 
    : 0;
  const isExpired = user.role === 'MODERATOR_FREE' 
    ? isTrialExpired(user.freeTrialStartedAt || null)
    : false;

  const getTrialChipProps = () => {
    if (isExpired) {
      return {
        color: 'error' as const,
        icon: <AlertTriangle size={16} />,
        label: 'Essai expiré'
      };
    } else if (daysRemaining <= 3) {
      return {
        color: 'warning' as const,
        icon: <Timer size={16} />,
        label: `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''} restant${daysRemaining > 1 ? 's' : ''}`
      };
    } else {
      return {
        color: 'info' as const,
        icon: <Timer size={16} />,
        label: `Essai: ${daysRemaining} jours`
      };
    }
  };

  return (
    <Box>
      {/* Trial Chip for MODERATOR_FREE users */}
      {user.role === 'MODERATOR_FREE' && (
        <Box sx={{
          p: collapsed ? 1 : 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50]
        }}>
          <Tooltip 
            title={
              isExpired 
                ? "Votre période d'essai est terminée. Cliquez pour souscrire."
                : `Votre période d'essai se termine dans ${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}. Cliquez pour voir les tarifs.`
            }
            placement="right"
          >
            <Chip
              {...getTrialChipProps()}
              onClick={() => navigate('/pricing')}
              clickable
              sx={{
                width: '100%',
                fontWeight: 'bold',
                fontSize: collapsed ? '0.75rem' : '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 2,
                },
                transition: 'all 0.3s ease',
                animation: daysRemaining <= 3 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Controls Section - Hidden on mobile */}
      <Box sx={{
        p: collapsed ? 1 : 2,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: theme.palette.mode === "dark"
          ? theme.palette.grey[900]
          : theme.palette.grey[50],
        // Masquer sur mobile
        '@media (max-width: 768px)': {
          display: 'none',
        }
      }}>
        {collapsed ? (
          // Mode collapsed : afficher seulement les icônes
          <Stack spacing={1} alignItems="center">
            <Tooltip title={mode === "dark" ? "Mode clair" : "Mode sombre"} placement="right">
              <IconButton
                onClick={() => setMode()}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  },
                  width: 40,
                  height: 40,
                }}
              >
                {mode === "dark" ? <Sun size={20} /> : <Moon size={20} />}
              </IconButton>
            </Tooltip>

            <Tooltip title={currentLocale === 'fr' ? 'English' : 'Français'} placement="right">
              <IconButton
                onClick={() => {
                  // Toggle between languages
                  const languages = i18n.languages ?? [];
                  const current = currentLocale || 'en';
                  const currentIndex = languages.indexOf(current);
                  const nextIndex = (currentIndex + 1) % languages.length;
                  changeLanguage(languages[nextIndex]);
                }}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  },
                  width: 40,
                  height: 40,
                  padding: 0,
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  }}
                  src={`/images/flags/${currentLocale || 'en'}.svg`}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          // Mode normal : afficher avec labels
          <Stack spacing={2}>
            {/* Dark Mode Toggle */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {translate('settings.darkMode')}
              </Typography>
              <IconButton
                onClick={() => setMode()}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '20'
                  }
                }}
              >
                {mode === "dark" ? <Sun size={20} /> : <Moon size={20} />}
              </IconButton>
            </Stack>

            {/* Language Selection */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {translate('settings.language')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={currentLocale}
                  onChange={(e) => changeLanguage(e.target.value)}
                  variant="outlined"
                  sx={{
                    height: 32,
                    '& .MuiSelect-select': {
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                >
                  {[...(i18n.languages ?? [])].sort().map((lang: string) => (
                    <MenuItem key={lang} value={lang}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 0,
                          }}
                          src={`/images/flags/${lang}.svg`}
                        />
                        <Typography variant="body2">
                          {lang === "en" ? "EN" : lang === "fr" ? "FR" : lang === "es" ? "ES" : lang === "pt" ? "PT" : lang.toUpperCase()}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* User Profile Section */}
      <ButtonBase
        onClick={handleClick}
        sx={{
          width: "100%",
          p: collapsed ? 1 : 2,
          borderBottom: 1,
          borderColor: "divider",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? theme.palette.grey[800]
              : theme.palette.grey[50],
          },
          // Sur mobile, supprimer la bordure en bas car c'est le dernier élément visible
          '@media (max-width: 768px)': {
            borderBottom: 'none',
          }
        }}
      >
        {collapsed ? (
          // Mode collapsed : afficher seulement l'avatar
          <Tooltip title={user.fullname || user.email} placement="right">
            <Avatar
              src={user.profilePicture}
              alt={user.fullname || user.email}
              sx={{ width: 32, height: 32 }}
            />
          </Tooltip>
        ) : (
          // Mode normal : afficher le profil complet
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
            <Avatar
              src={user.profilePicture}
              alt={user.fullname || user.email}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  lineHeight: 1.2,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {user.fullname || user.email}
              </Typography>
              {user.role && (
                <Chip
                  label={user.role}
                  size="small"
                  color={getRoleColor(user.role)}
                  sx={{
                    height: 18,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    mt: 0.5,
                  }}
                />
              )}
            </Box>
          </Stack>
        )}
      </ButtonBase>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          sx={{
            padding: "0 !important",
            paddingTop: "0 !important",
            mt: 0,
            backgroundColor: theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.background.paper,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate(`/users/${user.id}`)}
              sx={{
                color: 'primary.main',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                },
              }}
            >
              <ListItemIcon>
                <UserIcon size={18} style={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText>Mon profil</ListItemText>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                color: 'error.main',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                },
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                },
              }}
            >
              <ListItemIcon>
                <LogOut size={18} style={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText>Déconnexion</ListItemText>
            </ListItemButton>
          </ListItem>
        </List>
      </Collapse>
    </Box>
  );
};
